import React from 'react';
import { createPortal as domCreatePortal } from 'react-dom';

import { isArray, isFunction, isNumber, isString, isUndefined } from 'utils/check';
import { AnchorPopup, Avatar, TextField, useClickOutside } from 'ui';
import { CloserCrossIcon } from 'ui/icons';

import clearClasses from './ClearSelect.module.scss';
import filledClasses from './FilledSelect.module.scss';
import outlinedClasses from './OutlinedSelect.module.scss';
import type { SelectProps } from './types';
import { useSelectView } from './useSelectView';

import type { FormValue } from 'form';

const rootClassesMap = {
  filled: filledClasses,
  outlined: outlinedClasses,
  clear: clearClasses,
  spaces: {},
};

export const SelectInternal = <OPTION_TYPE extends Record<string, FormValue>>(
  props: SelectProps<OPTION_TYPE>,
) => {
  const {
    options,
    optionPropsMap,
    multiple = false,
    bricks = false,
    hasImage = false,
    forceTooltip = false,
    value,
    createPortal,
    clickOutsideIgnore = [],
    imperativeParams,
    popupMinWidth,
    popupMaxHeight,
    popupPlacement,
    onChange,
    onSearch,
    loading,
    disabled,
    readOnly,
  } = props;

  const [popupOpened, setPopupOpened] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<FormValue>(null);
  const [internalOptions, setInternalOptions] = React.useState<OPTION_TYPE[]>([]);
  const [searchFilter, setSearchFilter] = React.useState<string>('');
  const deferredSearchFilter = React.useDeferredValue(searchFilter);
  const inputContainerRef = React.useRef<HTMLDivElement>(null);
  const popupContainerRef = React.useRef<HTMLDivElement>(null);

  const {
    textFieldProps,
    bricksContainerProps,
    bricksPlaceholderProps,
    brickProps,
    brickCloserContainerProps,
    popupContainerProps,
    popupPaperProps,
    popupOptionsListProps,
    popupOptionProps,
    popupOptionInfoProps,
    popupOptionTextContainerProps,
    popupOptionTitleProps,
    popupOptionSubTitleProps,
    popupOptionIndicatorProps,
  } = useSelectView(props, rootClassesMap[props.variant || 'outlined'], {
    popupOpened,
  });

  useClickOutside([inputContainerRef, popupContainerRef, ...clickOutsideIgnore], () => {
    setPopupOpened(false);
    setSearchFilter('');
  });

  React.useEffect(() => {
    if (imperativeParams) {
      imperativeParams.current = {
        inputContainerRef,
        popupContainerRef,
      };
    }
  }, []);

  React.useEffect(() => {
    if (!isUndefined(value) && !loading) setInternalValue(value);
  }, [value, loading]);

  React.useEffect(() => {
    if (isFunction(onSearch)) {
      onSearch(deferredSearchFilter);
      return;
    }

    setInternalOptions(
      options.filter((option) => {
        const optionRawViewValue = isFunction(optionPropsMap.viewValue)
          ? optionPropsMap.viewValue(option)
          : option[optionPropsMap.viewValue];

        const optionViewValue =
          isString(optionRawViewValue) &&
          optionRawViewValue.trim().toLowerCase().replace(/е/g, 'е');

        const searchValue = deferredSearchFilter.trim().toLowerCase().replace(/е/g, 'е');

        if (isString(optionViewValue)) return optionViewValue.includes(searchValue);
        else return option;
      }),
    );
  }, [options, deferredSearchFilter, onSearch]);

  const handleToggle = React.useCallback(() => {
    if (disabled || readOnly) return;
    setPopupOpened((prev) => {
      if (prev) setSearchFilter('');
      return !prev;
    });
  }, [disabled, readOnly]);

  const handleOptionClick = React.useCallback(
    (option: OPTION_TYPE) => {
      const optionKey = isFunction(optionPropsMap.key)
        ? optionPropsMap.key(option)
        : option[optionPropsMap.key];

      setInternalValue((prev) => {
        const getFinalValue = () => {
          if (isString(optionKey) || isNumber(optionKey)) {
            if (multiple && prev === null) {
              return [optionKey];
            }

            if (
              multiple &&
              isArray(prev) &&
              (isString(optionKey) || isNumber(optionKey))
            ) {
              const v = prev.includes(optionKey)
                ? prev.filter((item) => item !== optionKey)
                : [...prev, optionKey];

              if (!v.length) return null;

              return v;
            }
          }

          if (prev === optionKey) return null;
          return optionKey;
        };

        const finalValue = getFinalValue();

        onChange?.(finalValue);
        return finalValue;
      });

      setPopupOpened(false);
    },
    [multiple, optionPropsMap],
  );

  let textViewValue: string = '';

  if (!bricks) {
    if (isArray(internalValue)) {
      if (internalValue.length > 1) {
        textViewValue = `Выбрано значений: ${internalValue.length}`;
      } else {
        const founded = internalOptions.find(
          (option) =>
            (isFunction(optionPropsMap.key)
              ? optionPropsMap.key(options)
              : option[optionPropsMap.key]) === internalValue[0],
        );

        const v = isFunction(optionPropsMap.viewValue)
          ? optionPropsMap.viewValue(founded || {})
          : founded?.[optionPropsMap.viewValue];

        if (isString(v)) textViewValue = v;
      }
    } else if (isString(internalValue) || isNumber(internalValue)) {
      const founded = internalOptions.find(
        (option) =>
          (isFunction(optionPropsMap.key)
            ? optionPropsMap.key(options)
            : option[optionPropsMap.key]) === internalValue,
      );

      textViewValue = String(
        isFunction(optionPropsMap.viewValue)
          ? optionPropsMap.viewValue(founded || {})
          : founded?.[optionPropsMap.viewValue] || '',
      );
    } else {
      textViewValue = '';
    }
  }

  const textFieldNativeTooltip =
    (!bricks && isArray(internalValue) && internalValue.length > 1
      ? internalOptions
          .filter(
            (option) =>
              (isFunction(optionPropsMap.key)
                ? optionPropsMap.key(option)
                : option[optionPropsMap.key]) &&
              internalValue.includes(
                isFunction(optionPropsMap.key)
                  ? optionPropsMap.key(option)
                  : (option[optionPropsMap.key] as string | number),
              ),
          )
          .map((option) =>
            isFunction(optionPropsMap.viewValue)
              ? optionPropsMap.viewValue(option)
              : option[optionPropsMap.viewValue],
          )
          .join(', ')
      : forceTooltip && String(internalValue)) || undefined;

  const renderBricksInput = bricks
    ? (inputClassName?: string) => {
        return (
          <div className={inputClassName} style={{ paddingLeft: '4px', height: '100%' }}>
            <div className={bricksContainerProps.className}>
              {isArray(internalValue) && !internalValue.length && (
                <span className={bricksPlaceholderProps.className}>
                  {readOnly ? 'Ничего не выбрано' : 'Выбрать из списка'}
                </span>
              )}
              {isArray(internalValue) &&
                internalOptions.map((option, idx) => {
                  const key = isFunction(optionPropsMap.key)
                    ? optionPropsMap.key(option)
                    : option[optionPropsMap.key];

                  if ((isString(key) || isNumber(key)) && !internalValue.includes(key))
                    return null;

                  const viewValue = isFunction(optionPropsMap.viewValue)
                    ? optionPropsMap.viewValue(option)
                    : option[optionPropsMap.viewValue];

                  return (
                    <div
                      key={isString(key) ? key : idx}
                      className={brickProps.className}
                      title={(isString(viewValue) && viewValue) || ''}
                    >
                      <span>{viewValue?.toString()}</span>
                      <div
                        className={brickCloserContainerProps.className}
                        onClick={() => !readOnly && handleOptionClick(option)}
                      >
                        <CloserCrossIcon />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        );
      }
    : undefined;

  let anchorEl = !readOnly && (
    <AnchorPopup
      ref={popupContainerRef}
      anchorRef={inputContainerRef}
      enabled={popupOpened}
      minWidth={popupMinWidth}
      height={popupMaxHeight}
      placement={popupPlacement}
    >
      <div className={popupContainerProps.className}>
        <div className={popupPaperProps.className}>
          {onSearch && (
            <TextField
              value={searchFilter}
              onChange={setSearchFilter}
              placeholder={'Поиск'}
              variant={'outlined'}
            />
          )}
          <div className={popupOptionsListProps.className}>
            {internalOptions.map((option, idx) => {
              if (!Object.keys(optionPropsMap).length) return null;

              const key = isFunction(optionPropsMap.key)
                ? optionPropsMap.key(option)
                : option[optionPropsMap.key];

              const title = isFunction(optionPropsMap.title)
                ? optionPropsMap.title(option)
                : option[optionPropsMap.title];

              const subtitle =
                optionPropsMap.subTitle &&
                (isFunction(optionPropsMap.subTitle)
                  ? optionPropsMap.subTitle(option)
                  : option[optionPropsMap.subTitle]);

              const avatarSrc =
                optionPropsMap.imageSrc &&
                (isFunction(optionPropsMap.imageSrc)
                  ? optionPropsMap.imageSrc(option)
                  : option[optionPropsMap.imageSrc]);

              const avatarPlaceholder =
                optionPropsMap.imagePlaceholderSrc &&
                (isFunction(optionPropsMap.imagePlaceholderSrc)
                  ? optionPropsMap.imagePlaceholderSrc(option)
                  : option[optionPropsMap.imagePlaceholderSrc]);

              return (
                <div
                  key={isString(key) ? key : idx}
                  className={popupOptionProps.className}
                  onClick={() => handleOptionClick(option)}
                >
                  <div className={popupOptionInfoProps.className}>
                    {hasImage && isString(avatarSrc) && (
                      <Avatar
                        src={avatarSrc}
                        fallbackId={
                          isNumber(Number(avatarPlaceholder))
                            ? Number(avatarPlaceholder)
                            : undefined
                        }
                        size={'sm'}
                      />
                    )}
                    <div className={popupOptionTextContainerProps.className}>
                      <p className={popupOptionTitleProps.className}>
                        {title?.toString()}
                      </p>
                      {subtitle && (
                        <p className={popupOptionSubTitleProps.className}>
                          {subtitle?.toString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      isArray(internalValue)
                        ? popupOptionIndicatorProps.className?.(
                            internalValue.includes(key as string | number),
                          )
                        : popupOptionIndicatorProps.className?.(internalValue === key)
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AnchorPopup>
  );

  if (createPortal) anchorEl = domCreatePortal(anchorEl, document.body);

  return (
    <>
      <TextField
        {...textFieldProps}
        value={(isString(textViewValue) && textViewValue) || ''}
        fieldOnClick={handleToggle}
        inputContainerRef={inputContainerRef}
        loading={textFieldProps.loading}
        nativeTooltip={textFieldNativeTooltip}
        input={renderBricksInput}
      />
      {anchorEl}
    </>
  );
};

export const Select = React.memo(SelectInternal) as typeof SelectInternal;
